// screenCapture.ts
// Asks the browser for the screen and accepts ONLY "Entire Screen".
//
// What a web page can and cannot do:
//  - It can PRESELECT the "Entire Screen" tab in the browser's picker and hide the "share this tab instead" switch.
//  - It cannot hide the Tab / Window options (no browser allows that).
//  - So we check what was actually chosen and, if it is a tab or window, stop it immediately and report it.
//  - Browsers that do not report the surface type (e.g. Safari) cannot be verified, so they are accepted.

export type ScreenRejectReason = "not_entire_screen";

export interface InitializeScreenShareOptions {
  /** Called when the student picked a tab or a window. The stream has ALREADY been stopped. */
  onRejected?: (reason: ScreenRejectReason) => void;
}

export interface EntireScreenResult {
  stream: MediaStream | null;
  rejected: ScreenRejectReason | null;
}

export function getDisplaySurface(stream: MediaStream): string | undefined {
  const settings = stream.getVideoTracks()[0]?.getSettings() as { displaySurface?: string } | undefined;
  return settings?.displaySurface;
}

export async function requestEntireScreen(): Promise<EntireScreenResult> {
  // Hints understood by Chrome/Edge; other browsers ignore unknown options.
  const hinted = {
    video: { frameRate: { ideal: 15, max: 30 }, displaySurface: "monitor" },
    audio: false,
    selfBrowserSurface: "exclude", // do not offer the exam tab itself
    surfaceSwitching: "exclude", // hide "share this tab instead"
    monitorTypeSurfaces: "include",
  } as DisplayMediaStreamOptions;

  const basic: DisplayMediaStreamOptions = {
    video: { frameRate: { ideal: 15, max: 30 } },
    audio: false,
  };

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia(hinted);
  } catch (err) {
    const name = (err as { name?: string } | null)?.name;
    // The student cancelled or denied: report it, do not ask again silently.
    if (name === "NotAllowedError" || name === "AbortError") throw err;
    // Some browser rejected the hint options: retry with the plain request.
    stream = await navigator.mediaDevices.getDisplayMedia(basic);
  }

  const surface = getDisplaySurface(stream);
  if (surface && surface !== "monitor") {
    stream.getTracks().forEach((t) => t.stop()); // really stop it: the browser's "sharing" bar disappears
    return { stream: null, rejected: "not_entire_screen" };
  }
  return { stream, rejected: null };
}